import autofocus from "vue-autofocus-directive";
import Buefy from "buefy"; // Leave this, it makes Buefy work everywhere
import Vue2Filters from 'vue2-filters';
import format from 'date-fns/format';
import {enGB, enUS, enCA, enIN, } from 'date-fns/locale';
import cdnHelper from "~/mixins/cdn-helper";

import BaseButton from "~/components/BaseButton.vue";
import BaseBuefyButton from "~/components/BaseBuefyButton.vue";
import BaseBuefyInput from "./components/BaseBuefyInput.vue";
import BaseInput from "./components/BaseInput.vue";
import BaseImage from "~/components/BaseImage.vue";
import BaseLink from "~/components/BaseLink.vue";
import BaseVideo from "~/components/BaseVideo.vue";
import BaseBuefyImage from "~/components/BaseBuefyImage.vue";

import markdownSanitizer from "~/mixins/markdown-sanitizer";

//Register global attributes to be present in all vue components
Vue.prototype.isLoggedIn = loggedIn;
Vue.prototype.cdnBase = cdnBase;

const dateFormatLocales = {
	"en_US": enUS,
	"en_GB": enGB,
	"en_IN": enIN,
	"en_CA": enCA,
};

Vue.use(Vue2Filters);

/**
 * Initializes the global Vue clickstream client when the server-side Hightouch
 * feature flag is enabled and the Hightouch browser wrapper has loaded.
 *
 * Vue components use `this.$clickstream` defensively, so the prototype property
 * is only created when the factory is available.
 */
let clickstream = null;
if(typeof hightouchEnabled !== "undefined" && hightouchEnabled) {

	const { createHightouchEventFactory } = window.HightouchClickstreamJS || {};

	if (createHightouchEventFactory) {
		clickstream = createHightouchEventFactory({
			writeKey: hightouchWriteKey,
			initOptions: {
				apiHost: hightouchApiHost,
			},
			autoPage: true,
			eventDedupeWindowMs: 300
		});
		Vue.prototype.$clickstream = clickstream;
		console.log("HightouchClickstreamJS loaded");
	} else {
		console.error("HightouchClickstreamJS not loaded");
	}
}

/**
 * Checks whether the browser currently has an exact cookie name/value pair.
 *
 * This is used for simple server-set event flags, such as `loginEvent=true`,
 * where the frontend only needs to know whether the event should be processed.
 *
 * @param {string} name The cookie name to match.
 * @param {string} value The expected cookie value.
 * @returns {boolean} True when `document.cookie` contains `${name}=${value}`.
 */
function hasCookie(name, value) {
	return document.cookie
		.split("; ")
		.some(cookie => cookie === `${name}=${value}`);
}

/**
 * Expires a cookie at the root path so it cannot trigger future page loads.
 *
 * @param {string} name The name of the cookie to clear.
 * @returns {void}
 */
function clearCookie(name) {
	document.cookie = `${name}=; Max-Age=0; path=/`;
}

/**
 * Builds a display-ready full name from the user payload.
 *
 * Missing, non-string, or blank first/last names intentionally return `null`
 * so downstream analytics do not receive partial or malformed name values.
 *
 * @param {?Object} user The current user object provided by the page template.
 * @param {string} [user.firstName] The user's first name.
 * @param {string} [user.lastName] The user's last name.
 * @returns {?string} The trimmed full name, or null when either name is absent.
 */
function getFullName(user) {
	const firstName = user && typeof user.firstName === "string" ? user.firstName.trim() : "";
	const lastName = user && typeof user.lastName === "string" ? user.lastName.trim() : "";

	return firstName && lastName ? `${firstName} ${lastName}` : null;
}

/**
 * Sends a one-time clickstream identity event immediately after login.
 *
 * The backend sets `loginEvent=true` as a short-lived browser signal. Once this
 * page sees a logged-in user and that flag, it identifies the customer in
 * Hightouch when the client is available, then clears the cookie in a `finally`
 * block so the event is not replayed on refresh or later navigation.
 */
if (loggedIn && hasCookie("loginEvent", "true")) {
	const currentUser = typeof user !== "undefined" ? user : null;

	try {
		if (clickstream && typeof clickstream.identify === "function" && currentUser && currentUser.customerId) {
			clickstream.identify(String(currentUser.customerId), {
				email: currentUser.username || null,
				name: getFullName(currentUser),
			});
		}
	} finally {
		clearCookie("loginEvent");
	}
}


/**
 * Resets the browser clickstream identity immediately after a successful logout.
 *
 * Spring Security sets `logoutEvent=true` as a short-lived browser signal from
 * the `/logout` handler, but only when the request had a real authenticated
 * principal. Once this anonymous post-logout page sees the flag, it resets the
 * Hightouch client identity when available, then clears the cookie in a
 * `finally` block so the reset is not replayed on refresh or later navigation.
 */
if (hasCookie("logoutEvent", "true")) {
	try {
		if (clickstream && typeof clickstream.reset === "function") {
			clickstream.reset();
		}
	} finally {
		clearCookie("logoutEvent");
	}
}

// CDN url helper mixin
Vue.mixin(cdnHelper);
Vue.mixin(markdownSanitizer);

const ogElements = ["og-offer", "og-when", "og-select-frequency"];
const ignoredElements = Array.isArray(Vue.config.ignoredElements)
  ? Vue.config.ignoredElements
  : [];

ogElements.forEach((tag) => {
	if (!ignoredElements.includes(tag)) {
		ignoredElements.push(tag);
	}
});

Vue.config.ignoredElements = ignoredElements;

// Configure Buefy
Vue.prototype["$buefy"].config.setOptions({
	defaultIconPack: "fal",
	customIconPacks: {
		"fas": {
			sizes: {
				"default": "",
				"is-small": "fa-xs",
				"is-medium": "fa-lg",
				"is-large": "fa-2x"
			}
		},
		"fal": {
			sizes: {
				"default": "",
				"is-small": "fa-xs",
				"is-medium": "fa-lg",
				"is-large": "fa-2x"
			}
		},
		"far": {
			sizes: {
				"default": "",
				"is-small": "fa-xs",
				"is-medium": "fa-lg",
				"is-large": "fa-2x"
			}
		}
	}
});

/*
	Groups like pages so that we can obtain overall performance metrics for the group
	rather than for each unique URL
 */
const pathPrefixesToGroup = [
	'/content/blocks/',
	'/health-professionals/',
	'/products/dp/',
	'/products/by-ingredient/',
	'/quizzes/',
	'/take-5-daily/article/',
	'/take-5-daily/video/',
	'/take-5-daily/podcast/',
	'/u/'
]
function sanitizeUrlPath() {
	let matchIx = pathPrefixesToGroup.findIndex(prefix => location.pathname.startsWith(prefix))
	return matchIx >= 0 ? `${pathPrefixesToGroup[matchIx]}*` : location.pathname
}

//Set some axios defaults
axios.defaults.headers.common["Content-Type"] = "application/json";

function setAxiosHeader(headers, key, value) {
	if (!headers) {
		return;
	}

	if (typeof headers.set === "function") {
		headers.set(key, value);
		return;
	}

	headers[key] = value;
}

function removeAxiosHeader(headers, key) {
	if (!headers) {
		return;
	}

	if (typeof headers.delete === "function") {
		headers.delete(key);
	} else {
		delete headers[key];
	}
}

// Add CSRF and X-Requested-With headers only for same-origin requests
axios.interceptors.request.use(function(config) {
	const requestUrl = config.url || "";
	const isSameOrigin = requestUrl.startsWith(window.location.origin) || requestUrl.startsWith("/");
    if (isSameOrigin) {
        const csrfMeta = document.querySelector("meta[name='_csrf']");
        if (csrfMeta) {
			setAxiosHeader(config.headers, "X-CSRF-TOKEN", csrfMeta.getAttribute("content"));
        }
		setAxiosHeader(config.headers, "X-Requested-With", "XMLHttpRequest");
    } else {
        // Remove CSRF and X-Requested-With headers if present
		removeAxiosHeader(config.headers, "X-CSRF-TOKEN");
		removeAxiosHeader(config.headers, "X-Requested-With");
    }
    return config;
}, function(error) {
    return Promise.reject(error);
});

// Create floating label directive
Vue.directive("floating-label", {
    inserted: function (el) {
        // Add a class for styling
        el.classList.add("has-floating-label");

        // Move the label after the input (so we can do pure CSS floating label styling)
        const label = el.querySelector("label");
        let input = el.querySelector("input");
        if (!input) {
        	input = el.querySelector("textarea");
        }
        if (!input) {
        	input = el.querySelector("select");
        }

        if (label && input) {
        	input.parentElement.insertBefore(label, input.nextSibling);

        	// Because I'm lazy and don't want to have to set the invisible placeholder all the time, copy the label into the placeholder
        	input.placeholder = label.textContent;
        }

    }
});

Vue.directive("fix-dropdown-role", {
	inserted: function (el) {
		// Find Buefy dropdown trigger with tabindex=0 but no role
		const trigger = el.querySelector('.dropdown-trigger[tabindex="0"]:not([role])');
		if (trigger) {
			trigger.setAttribute('role', 'button');
		}
	}
});

Vue.directive("add-dropdown-role", {
	inserted(el) {
		Vue.nextTick(() => {
			const trigger = el.querySelector('.dropdown-trigger');
			if (trigger) {
				trigger.setAttribute('role', 'button');
				trigger.setAttribute('tabindex', '0');
				trigger.setAttribute('aria-haspopup', 'true');
			}
		});
	}
});

Vue.directive("positive-integer-only", {
  inserted(el) {
    const input = el.querySelector("input");
    if (!input) return;

    input.addEventListener("input", () => {
		let val = input.value.replaceAll(/\D+/g, "");
		input.value = val;
		input.dispatchEvent(new Event("input"));
    });
  }
});

//Add a filter allowing to format the currency based on locale
Vue.filter("toCurrency", function (value) {
	let result = value;
    if (!isNaN(parseFloat(value))) {
    	let loc = pageLocale.replace("_", "-");
    	var formatter = new Intl.NumberFormat(loc, {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 0
        });
        result = formatter.format(value);
    }
    return result;
});

//Add a filter allowing to format a date based on locale and/or format
Vue.filter("dateFormat", function (value, defaultResult, dtFormat) {
	let result = defaultResult;
	if (value) {
		let tmpDate = new Date(value);
		if (tmpDate !== "Invalid Date" && !isNaN(tmpDate)) {
			let loc = pageLocale.replace("_", "-");
			const dateFnsLocale = dateFormatLocales[pageLocale];
	    	if (dtFormat && dateFnsLocale) {
				result = format(tmpDate, dtFormat, {locale: enUS});
	    	} else {
	    		result = tmpDate.toLocaleDateString(loc);
	    	}
		}
	}
    return result;
});

/*
* This component is useful for rendering inline html, without including a wrapper element.
* For example, say we had the following html content:
*
* <span>Some cool stuff here</span>
*
*
* Using the following would insert the div into the DOM as well:
*
* <div v-html="content"></div>
* ... becomes ...
* <div><span>Some cool stuff here</span></div>
*
*
* This component allows us to include the html content without a wrapper:
*
* <html-fragment :html="content"></html-fragment>
* ... becomes ...
* <span>Some cool stuff here</span>
*
*
*/
Vue.component('html-fragment', {
	functional: true,
	props: ['html'],
	render(h, ctx) {
		if (ctx.props.html) {
			const html = ctx.props.html.trim();
			const nodes = new Vue({
				beforeCreate() {
					this.$createElement = h
				},
				template: `<div>${html}</div>`
			}).$mount()._vnode.children;
			return nodes;
		}
	}
});

Vue.component('BaseBuefyInput', BaseBuefyInput)
Vue.component('BaseInput', BaseInput)
// Register global components
Vue.component('BaseImage', BaseImage)
// Register global components
Vue.component('BaseVideo', BaseVideo)

// Global components
Vue.component('BaseLink', BaseLink)

Vue.directive("autofocus", autofocus);
Vue.component('BaseButton', BaseButton)
Vue.component('BaseBuefyButton', BaseBuefyButton)

Vue.component('BaseBuefyImage', BaseBuefyImage)

document.addEventListener("DOMContentLoaded", () => {
	const anchor = document.querySelector("#cart");
	const modal = document.querySelector("#newsletter-modal");
	const modalInput = document.querySelector("#newsletter-email-input");
	const closeModal = document.querySelector('#newsletter-close-button');

	if (!anchor || !modal || !modalInput) return;

	anchor.addEventListener("focusout", (event) => {
		setTimeout(() => {
			if (!modal.classList.contains("hidden")) {
				modalInput.focus();
			}
		}, 0);
	});

	closeModal.addEventListener('click', event => {
		anchor.focus()
	});
});
